import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { SplitPaneLayout } from '../../../components/SplitPaneLayout';
import { formatDateTime } from '../../../lib/datetime';
import type { PipelineRunsResponse, SourceSystemResource } from '../types/source-system';
import { PipelineRunDetail, PipelineRunRow, PipelineRunTableHeader } from './PipelineRunRow';

interface PipelineRunsViewerProps {
  resource: SourceSystemResource | null;
  data: PipelineRunsResponse | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

/**
 * Right-hand content pane of the PipelineBrowser.
 *
 * Shows:
 * - Definition metadata (pipeline name, category, last sync, sync counters)
 * - Recent runs via SplitPaneLayout: clickable run list on the left,
 *   run detail (stages, artifacts, failure log) on the right.
 */
export function PipelineRunsViewer({
  resource,
  data,
  loading,
  error,
  onRetry,
}: PipelineRunsViewerProps) {
  const [selectedRunUuid, setSelectedRunUuid] = React.useState<string | null>(null);

  // Reset selection whenever the viewed pipeline changes
  React.useEffect(() => {
    setSelectedRunUuid(null);
  }, [resource?.resourceUuid]);

  if (!resource) {
    return (
      <div className="pl-viewer__empty">
        <p>Select a pipeline from the list to view its run history.</p>
      </div>
    );
  }

  const categoryLabel = data?.pipelineCategory === 'release' ? 'Release' : 'Build';
  const runs = data?.runs ?? [];
  const selectedRun = selectedRunUuid
    ? (runs.find((r) => r.uuid === selectedRunUuid) ?? null)
    : null;

  return (
    <div className="pl-viewer">
      {/* ── Definition section ───────────────────────────────────────── */}
      <div className="pl-viewer__definition">
        <div className="pl-viewer__def-header">
          <div className="pl-viewer__def-title-row">
            <h3 className="pl-viewer__def-name">{resource.providerResourceName}</h3>
            <span className="pl-viewer__category-badge">{categoryLabel}</span>
          </div>
          {resource.parentProviderResourceId && (
            <p className="pl-viewer__def-project">{resource.parentProviderResourceId}</p>
          )}
        </div>

        <dl className="pl-viewer__def-meta">
          <div className="pl-viewer__def-row">
            <dt>Last sync</dt>
            <dd>{formatDateTime(resource.lastRunAt)}</dd>
          </div>
          {resource.recordsFetched > 0 && (
            <>
              <div className="pl-viewer__def-row">
                <dt>Fetched</dt>
                <dd>{resource.recordsFetched}</dd>
              </div>
              <div className="pl-viewer__def-row">
                <dt>New</dt>
                <dd>{resource.recordsCreated}</dd>
              </div>
              <div className="pl-viewer__def-row">
                <dt>Updated</dt>
                <dd>{resource.recordsUpdated}</dd>
              </div>
              {resource.recordsFailed > 0 && (
                <div className="pl-viewer__def-row pl-viewer__def-row--warn">
                  <dt>Failed</dt>
                  <dd>{resource.recordsFailed}</dd>
                </div>
              )}
            </>
          )}
        </dl>
      </div>

      {/* ── Runs split layout ─────────────────────────────────────────── */}
      <SplitPaneLayout
        loading={loading}
        hasError={!!error}
        page={1}
        totalPages={1}
        onPageChange={() => undefined}
        selectedId={selectedRunUuid}
        detailTitle={
          selectedRun ? (
            <span className="pl-viewer__detail-title">{selectedRun.runName}</span>
          ) : null
        }
        detailContent={selectedRun ? <PipelineRunDetail run={selectedRun} /> : null}
        onCloseDetail={() => setSelectedRunUuid(null)}
        dividerAriaLabel="Resize run detail pane"
        defaultDetailWidth={400}
      >
        {loading && <div className="pl-viewer__state">Loading runs…</div>}
        {!loading && error && (
          <PageErrorState message={`Failed to load runs: ${error}`} onRetry={onRetry} />
        )}
        {!loading && !error && runs.length === 0 && (
          <div className="pl-viewer__state">
            No runs ingested yet. Trigger a sync to populate run history.
          </div>
        )}
        {!loading && !error && runs.length > 0 && (
          <div className="ch-table" role="table">
            <PipelineRunTableHeader />
            <div className="ch-table__body" role="rowgroup">
              {runs.map((run) => (
                <PipelineRunRow
                  key={run.uuid}
                  run={run}
                  selected={run.uuid === selectedRunUuid}
                  onSelect={setSelectedRunUuid}
                />
              ))}
            </div>
          </div>
        )}
      </SplitPaneLayout>
    </div>
  );
}
